/**
 * Account Backup Service
 *
 * Handles export/import of Fairdrop accounts via encrypted keystore files.
 * Supports local file download and Swarm storage with gasless ENS updates.
 */

import {
  createFairdropKeystore,
  parseFairdropKeystore,
  validateKeystoreFormat,
  getKeystoreSubdomain,
} from './keystore'
import { uploadData } from '../swarm/upload'
import { downloadData } from '../swarm/download'
import { getBackupHash, getFairdropPublicKey, ENS_DOMAIN } from '../ens'
import { fdsIdEnabled, getFdsIdClient } from '../fds-id/client'
import type { Account, BackupResult, KeystoreProgress } from '../../types'

// fds-id format decryption dependencies
import { scrypt } from '@noble/hashes/scrypt.js'
import { gcm } from '@noble/ciphers/aes.js'

// Gasless backup hash API endpoint
const BACKUP_HASH_API = (import.meta.env.VITE_BACKUP_HASH_API as string) || '/api/backup-hash'

// ============================================================
// Hex conversion helpers
// ============================================================

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return bytes
}

// ============================================================
// Swarm hash encryption (for ENS privacy)
// ============================================================

/**
 * Encrypt Swarm hash before storing in ENS
 * Uses AES-GCM with key derived from password + subdomain via SHA-256
 */
async function encryptSwarmHash(
  swarmHash: string,
  password: string,
  subdomain: string
): Promise<string> {
  // Derive key from password + subdomain (salt) using SHA-256
  const salt = new TextEncoder().encode(`fairdrop-backup:${subdomain}`)
  const passwordBytes = new TextEncoder().encode(password)
  const combined = new Uint8Array([...passwordBytes, ...salt])
  const keyMaterial = await crypto.subtle.digest('SHA-256', combined)

  // Import as AES-GCM key
  const key = await crypto.subtle.importKey('raw', keyMaterial, 'AES-GCM', false, ['encrypt'])

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12))

  // Encrypt the hash
  const hashBytes = new TextEncoder().encode(swarmHash)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, hashBytes)

  // Combine IV + ciphertext and encode as hex
  const result = new Uint8Array([...iv, ...new Uint8Array(ciphertext)])
  return bytesToHex(result)
}

/**
 * Decrypt Swarm hash retrieved from ENS
 * Supports both Fairdrop format and fds-id format
 */
async function decryptSwarmHash(
  encryptedHash: string,
  password: string,
  subdomain: string
): Promise<string> {
  const data = hexToBytes(encryptedHash)

  // Detect format by length:
  // - fds-id: 92+ bytes (32 salt + 12 nonce + 32 hash + 16 tag)
  // - Fairdrop: shorter (12 IV + ciphertext)
  if (data.length >= 92) {
    // fds-id format: [32-byte salt][12-byte nonce][ciphertext][16-byte tag]
    return decryptFdsIdFormat(data, password, subdomain)
  } else {
    // Fairdrop format: [12-byte IV][ciphertext]
    return decryptFairdropFormat(data, password, subdomain)
  }
}

/**
 * Decrypt using fds-id format (scrypt + AES-GCM)
 * Tries multiple password/AAD combinations for compatibility:
 * 1. Spec format: plain password with AAD (eth.fairdata.backup)
 * 2. fds-id format: password:subdomain without AAD
 */
async function decryptFdsIdFormat(
  data: Uint8Array,
  password: string,
  subdomain: string
): Promise<string> {
  // Extract components
  const salt = data.slice(0, 32)
  const nonce = data.slice(32, 44)
  const ciphertext = data.slice(44)

  // Try spec-compliant format first: plain password with AAD
  try {
    const derivedKey = scrypt(
      new TextEncoder().encode(password),
      salt,
      { N: 262144, r: 8, p: 1, dkLen: 32 }
    )
    const aad = new TextEncoder().encode(subdomain.toLowerCase())
    const cipher = gcm(derivedKey, nonce, aad)
    const plaintext = cipher.decrypt(ciphertext)
    return bytesToHex(plaintext)
  } catch {
    // Fall through to try fds-id format
  }

  // Try fds-id format: password:subdomain without AAD
  try {
    const passwordWithContext = `${password}:${subdomain.toLowerCase()}`
    const derivedKey = scrypt(
      new TextEncoder().encode(passwordWithContext),
      salt,
      { N: 262144, r: 8, p: 1, dkLen: 32 }
    )
    const cipher = gcm(derivedKey, nonce)
    const plaintext = cipher.decrypt(ciphertext)
    return bytesToHex(plaintext)
  } catch {
    throw new Error('Decryption failed - invalid password')
  }
}

/**
 * Decrypt using Fairdrop format (SHA-256 + AES-GCM)
 */
async function decryptFairdropFormat(
  data: Uint8Array,
  password: string,
  subdomain: string
): Promise<string> {
  // Derive key from password + subdomain (salt) using SHA-256
  const salt = new TextEncoder().encode(`fairdrop-backup:${subdomain}`)
  const passwordBytes = new TextEncoder().encode(password)
  const combined = new Uint8Array([...passwordBytes, ...salt])
  const keyMaterial = await crypto.subtle.digest('SHA-256', combined)

  // Import as AES-GCM key
  const key = await crypto.subtle.importKey('raw', keyMaterial, 'AES-GCM', false, ['decrypt'])

  // Split IV + ciphertext
  const iv = data.slice(0, 12)
  const ciphertext = data.slice(12)

  // Decrypt
  const hashBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)
  return new TextDecoder().decode(hashBytes)
}

export type BackupProgress =
  | KeystoreProgress
  | 'uploading'
  | 'downloading'
  | 'looking-up-ens'
  | 'updating-ens'

export interface ExportOptions {
  onProgress?: (stage: BackupProgress) => void
}

export interface ImportOptions {
  onProgress?: (stage: BackupProgress) => void
  /** Skip ENS verification (for offline imports) */
  skipVerification?: boolean
}

export interface ImportResult {
  account: Account
  /** Warning if ENS verification failed or pubkey mismatch */
  warning?: string
}

/**
 * Check if account already has a Swarm backup in ENS
 */
export async function hasExistingBackup(subdomain: string): Promise<boolean> {
  const ensName = `${subdomain}.${ENS_DOMAIN}`
  const existingHash = await getBackupHash(ensName)
  return existingHash !== null && existingHash.length > 0
}

/**
 * Set backup hash via gasless API
 * Server verifies the request and updates ENS with funded wallet
 */
async function setBackupHashGasless(
  username: string,
  swarmHash: string,
  publicKey: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(BACKUP_HASH_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        swarmHash,
        publicKey: publicKey.startsWith('0x') ? publicKey : `0x${publicKey}`,
      }),
    })

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { error?: string }
      throw new Error(errorData.error || `API request failed: ${response.status}`)
    }

    return { success: true }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    return { success: false, error: err.message }
  }
}

/**
 * Export account to encrypted keystore and trigger browser download
 */
export async function exportToFile(
  account: Account,
  password: string,
  options: ExportOptions = {}
): Promise<void> {
  const { onProgress } = options

  // Create encrypted keystore
  const keystoreJson = await createFairdropKeystore(account, password, onProgress)

  // Generate filename with subdomain and date
  const date = new Date().toISOString().split('T')[0]
  const filename = `${account.subdomain}-fairdrop-${date}.json`

  // Trigger browser download
  triggerJsonDownload(keystoreJson, filename)
}

/**
 * Export account to Swarm and update ENS with backup hash (gasless)
 *
 * Note: Only one Swarm backup per account is allowed. If a backup already
 * exists in ENS, this will return an error. Users can still download file
 * backups at any time.
 */
export async function exportToSwarm(
  account: Account,
  password: string,
  options: ExportOptions = {}
): Promise<BackupResult> {
  const { onProgress } = options

  try {
    // Check if backup already exists (max one per account)
    onProgress?.('looking-up-ens')
    const alreadyHasBackup = await hasExistingBackup(account.subdomain)

    if (alreadyHasBackup) {
      return {
        success: false,
        error: 'This mailbox already has a Swarm backup. Only one backup per account is allowed. You can still download a file backup.',
      }
    }

    // Create encrypted keystore
    const keystoreJson = await createFairdropKeystore(account, password, onProgress)

    // Get dedicated backup stamp (persists independently of user's upload stamp)
    let backupStampId: string | undefined
    if (fdsIdEnabled()) {
      try {
        const client = getFdsIdClient()
        backupStampId = (await client.getBackupStamp()) ?? undefined
      } catch {
        // Fall through to user's stamp
      }
    }

    // Upload to Swarm
    onProgress?.('uploading')
    const keystoreBytes = new TextEncoder().encode(keystoreJson)
    const swarmHash = await uploadData(keystoreBytes, {
      contentType: 'application/json',
      filename: `${account.subdomain}-fairdrop.json`,
      stampId: backupStampId,
    })

    // Encrypt the Swarm hash with password before storing in ENS (privacy)
    onProgress?.('updating-ens')
    const encryptedHash = await encryptSwarmHash(swarmHash, password, account.subdomain)
    const result = await setBackupHashGasless(account.subdomain, encryptedHash, account.publicKey)

    if (!result.success) {
      // Upload succeeded but ENS update failed
      // Return hash so user can manually set it later
      return {
        success: true,
        swarmHash,
        error: `Backup uploaded but ENS update failed: ${result.error}. Save this hash: ${swarmHash}`,
      }
    }

    return { success: true, swarmHash }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    return { success: false, error: err.message }
  }
}

/**
 * Import account from keystore file
 */
export async function importFromFile(
  file: File,
  password: string,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const { onProgress, skipVerification = false } = options

  // Read file content
  const keystoreJson = await file.text()

  // Parse and decrypt keystore
  const account = await parseFairdropKeystore(keystoreJson, password, onProgress)

  // Verify against ENS if account was registered
  let warning: string | undefined
  if (!skipVerification && account.subdomain) {
    try {
      const ensName = `${account.subdomain}.${ENS_DOMAIN}`
      const ensPublicKey = await getFairdropPublicKey(ensName)

      if (ensPublicKey && ensPublicKey !== account.publicKey) {
        warning = `Warning: This keystore's public key doesn't match the ENS registration for ${ensName}. This may be an old backup or a different account.`
      }
    } catch {
      // ENS lookup failed - offline or network error
      // Continue without verification
    }
  }

  return { account, warning }
}

/**
 * Import account from Swarm via ENS lookup
 * Expects encrypted Swarm reference in ENS (Fairdrop style)
 */
export async function importFromSwarm(
  subdomain: string,
  password: string,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const { onProgress } = options

  const ensName = `${subdomain}.${ENS_DOMAIN}`

  // Look up backup hash from ENS
  onProgress?.('looking-up-ens')
  const backupHash = await getBackupHash(ensName)

  if (!backupHash) {
    throw new Error(`No backup found for ${ensName}. The mailbox may not have a Swarm backup.`)
  }

  // Decrypt the Swarm reference (Fairdrop stores encrypted references)
  let swarmHash: string
  try {
    swarmHash = await decryptSwarmHash(backupHash, password, subdomain)
  } catch {
    throw new Error('Failed to decrypt backup reference. Check your password.')
  }

  // Download keystore from Swarm
  onProgress?.('downloading')
  const keystoreBytes = await downloadData(swarmHash)
  const keystoreJson = new TextDecoder().decode(keystoreBytes)

  // Validate basic structure before decryption
  try {
    const parsed = JSON.parse(keystoreJson)
    if (!validateKeystoreFormat(parsed)) {
      throw new Error('Downloaded file is not a valid Fairdrop keystore')
    }
  } catch (e) {
    if (e instanceof SyntaxError) {
      throw new Error('Downloaded backup is corrupted or not a valid keystore')
    }
    throw e
  }

  // Decrypt keystore
  const account = await parseFairdropKeystore(keystoreJson, password, onProgress)

  // Verify public key matches ENS (should always match for Swarm imports)
  const ensPublicKey = await getFairdropPublicKey(ensName)
  let warning: string | undefined

  if (ensPublicKey && ensPublicKey !== account.publicKey) {
    warning = `Warning: The backup's public key doesn't match the current ENS registration. This backup may be outdated.`
  }

  return { account, warning }
}

/**
 * Pre-fetched backup data (cached ENS lookup)
 * Since Swarm references are encrypted, we can only cache the ENS result
 */
export interface PrefetchedBackup {
  backupHash: string
}

/**
 * Pre-fetch ENS backup hash for faster import
 * The actual Swarm download requires the password to decrypt the reference
 */
export async function prefetchFromSwarm(
  subdomain: string
): Promise<PrefetchedBackup> {
  const ensName = `${subdomain}.${ENS_DOMAIN}`

  // Look up backup hash from ENS
  const backupHash = await getBackupHash(ensName)

  if (!backupHash) {
    throw new Error(`No backup found for ${ensName}. The mailbox may not have a Swarm backup.`)
  }

  return { backupHash }
}

/**
 * Import from pre-fetched ENS data (skips ENS lookup)
 */
export async function importFromPrefetched(
  prefetched: PrefetchedBackup,
  subdomain: string,
  password: string,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const { onProgress } = options

  const ensName = `${subdomain}.${ENS_DOMAIN}`

  // Decrypt the Swarm reference using cached backup hash
  let swarmHash: string
  try {
    swarmHash = await decryptSwarmHash(prefetched.backupHash, password, subdomain)
  } catch {
    throw new Error('Failed to decrypt backup reference. Check your password.')
  }

  // Download keystore from Swarm
  onProgress?.('downloading')
  const keystoreBytes = await downloadData(swarmHash)
  const keystoreJson = new TextDecoder().decode(keystoreBytes)

  // Validate basic structure before decryption
  try {
    const parsed = JSON.parse(keystoreJson)
    if (!validateKeystoreFormat(parsed)) {
      throw new Error('Downloaded file is not a valid Fairdrop keystore')
    }
  } catch (e) {
    if (e instanceof SyntaxError) {
      throw new Error('Downloaded backup is corrupted or not a valid keystore')
    }
    throw e
  }

  // Decrypt keystore
  const account = await parseFairdropKeystore(keystoreJson, password, onProgress)

  // Verify public key matches ENS
  const ensPublicKey = await getFairdropPublicKey(ensName)
  let warning: string | undefined

  if (ensPublicKey && ensPublicKey !== account.publicKey) {
    warning = `Warning: The backup's public key doesn't match the current ENS registration. This backup may be outdated.`
  }

  return { account, warning }
}

/**
 * Get subdomain from keystore file without decryption
 * Useful for displaying import preview
 */
export async function peekKeystoreSubdomain(file: File): Promise<string | null> {
  try {
    const json = await file.text()
    return getKeystoreSubdomain(json)
  } catch {
    return null
  }
}

/**
 * Validate keystore file structure without decryption
 */
export async function validateKeystoreFile(file: File): Promise<{
  valid: boolean
  subdomain?: string
  error?: string
}> {
  try {
    const json = await file.text()
    const parsed = JSON.parse(json)

    if (!validateKeystoreFormat(parsed)) {
      return { valid: false, error: 'Invalid keystore format' }
    }

    const subdomain = getKeystoreSubdomain(json)
    return { valid: true, subdomain: subdomain ?? undefined }
  } catch (e) {
    if (e instanceof SyntaxError) {
      return { valid: false, error: 'File is not valid JSON' }
    }
    return { valid: false, error: 'Unable to read file' }
  }
}

// ============================================================
// Private helpers
// ============================================================

/**
 * Trigger browser download of JSON content
 */
function triggerJsonDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up blob URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
